import React, { useState, useEffect } from 'react';
import { useAuth, UserProfile } from '../contexts/AuthContext';
import { useTheme, Theme, ThemeColors } from '../contexts/ThemeContext';
import { AdminStatistics } from './AdminStatistics';
import { PluginBannerManager } from './PluginBannerManager';
import { PluginVersionManager } from './PluginVersionManager';
import { PluginDiagnostic } from './PluginDiagnostic';
import { StorageBucketFix } from './StorageBucketFix';
import { StorageTest } from './StorageTest';

export const AdminPage: React.FC = () => {
  const { isAdmin, getAllUsers, grantDevAccess, revokeDevAccess } = useAuth();
  const { currentTheme, availableThemes, setActiveTheme, createTheme, refreshThemes } = useTheme();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'users' | 'themes' | 'statistics' | 'plugin-banners' | 'plugin-manager' | 'diagnostics' | 'storage'>('users');

  // Theme creation form state
  const [showCreateTheme, setShowCreateTheme] = useState(false);
  const [themeForm, setThemeForm] = useState({
    name: '',
    displayName: '',
    description: '',
    colors: {
      primary: '#3b82f6',
      secondary: '#8b5cf6',
      background: '#030712',
      surface: '#111827',
      surfaceLight: '#1f2937',
      text: '#ffffff',
      textSecondary: '#9ca3af',
      textMuted: '#6b7280',
      border: '#374151',
      borderLight: '#4b5563',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      accent: '#06b6d4'
    }
  });

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin]);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error } = await getAllUsers();
      if (error) {
        setError(error.message);
      } else if (data) {
        setUsers(data);
      }
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleGrantDevAccess = async (userId: string, username: string) => {
    setActionLoading(userId);
    setError(null);
    setSuccess(null);

    try {
      const { error } = await grantDevAccess(userId, `Dev access granted to @${username}`);
      if (error) {
        setError(error.message);
      } else {
        setSuccess(`Dev access granted to @${username}`);
        await loadUsers(); // Refresh users list
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError('Failed to grant dev access');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevokeDevAccess = async (userId: string, username: string) => {
    setActionLoading(userId);
    setError(null);
    setSuccess(null);

    try {
      const { error } = await revokeDevAccess(userId, `Dev access revoked from @${username}`);
      if (error) {
        setError(error.message);
      } else {
        setSuccess(`Dev access revoked from @${username}`);
        await loadUsers(); // Refresh users list
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError('Failed to revoke dev access');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSetActiveTheme = async (themeName: string) => {
    setActionLoading(themeName);
    setError(null);
    setSuccess(null);

    try {
      const { error } = await setActiveTheme(themeName);
      if (error) {
        setError(error.message);
      } else {
        setSuccess(`Theme changed successfully!`);
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError('Failed to change theme');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreateTheme = async () => {
    if (!themeForm.name || !themeForm.displayName) {
      setError('Theme name and display name are required');
      return;
    }

    setActionLoading('create-theme');
    setError(null);
    setSuccess(null);

    try {
      const { error } = await createTheme(
        themeForm.name,
        themeForm.displayName,
        themeForm.description,
        themeForm.colors
      );

      if (error) {
        setError(error.message);
      } else {
        setSuccess(`Theme "${themeForm.displayName}" created successfully!`);
        setShowCreateTheme(false);
        setThemeForm({
          name: '',
          displayName: '',
          description: '',
          colors: {
            primary: '#3b82f6',
            secondary: '#8b5cf6',
            background: '#030712',
            surface: '#111827',
            surfaceLight: '#1f2937',
            text: '#ffffff',
            textSecondary: '#9ca3af',
            textMuted: '#6b7280',
            border: '#374151',
            borderLight: '#4b5563',
            success: '#10b981',
            warning: '#f59e0b',
            error: '#ef4444',
            accent: '#06b6d4'
          }
        });
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError('Failed to create theme');
    } finally {
      setActionLoading(null);
    }
  };

  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getUserInitials = (user: UserProfile) => {
    if (user.full_name) {
      return user.full_name
        .split(' ')
        .map(name => name[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return user.username.slice(0, 2).toUpperCase();
  };

  const getRoleColor = (role?: string) => {
    switch (role) {
      case 'admin': return 'text-red-400 bg-red-900/30 border-red-600';
      default: return 'text-blue-400 bg-blue-900/30 border-blue-600';
    }
  };

  if (!isAdmin) {
    return (
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Access Denied</h2>
          <p className="text-gray-400">You need admin privileges to access this page.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Admin Panel</h1>
        <p className="text-gray-400 text-sm sm:text-base">Manage users, dev build access, plugins, and site themes.</p>
      </div>

      {/* Tabs - Mobile optimized with horizontal scroll */}
      <div className="mb-6">
        <div className="flex border-b border-gray-700 overflow-x-auto scrollbar-hide">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 sm:px-6 py-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base ${
              activeTab === 'users'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Users
          </button>
          <button
            onClick={() => setActiveTab('plugin-manager')}
            className={`px-4 sm:px-6 py-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base ${
              activeTab === 'plugin-manager'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <img 
              src="https://unpkg.com/pixelarticons@1.8.0/svg/upload.svg" 
              alt="Plugin Manager" 
              className="w-4 h-4 inline-block mr-2"
              style={{ filter: activeTab === 'plugin-manager' ? 'brightness(0) saturate(100%) invert(1)' : 'brightness(0) saturate(100%) invert(60%)' }}
            />
            <span className="hidden sm:inline">Plugin Manager</span>
            <span className="sm:hidden">Manager</span>
          </button>
          <button
            onClick={() => setActiveTab('plugin-banners')}
            className={`px-4 sm:px-6 py-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base ${
              activeTab === 'plugin-banners'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <img 
              src="https://unpkg.com/pixelarticons@1.8.0/svg/plugin.svg" 
              alt="Plugin Banners" 
              className="w-4 h-4 inline-block mr-2"
              style={{ filter: activeTab === 'plugin-banners' ? 'brightness(0) saturate(100%) invert(1)' : 'brightness(0) saturate(100%) invert(60%)' }}
            />
            <span className="hidden sm:inline">Plugin Banners</span>
            <span className="sm:hidden">Banners</span>
          </button>
          <button
            onClick={() => setActiveTab('themes')}
            className={`px-4 sm:px-6 py-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base ${
              activeTab === 'themes'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <span className="hidden sm:inline">Theme Management</span>
            <span className="sm:hidden">Themes</span>
          </button>
          <button
            onClick={() => setActiveTab('statistics')}
            className={`px-4 sm:px-6 py-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base ${
              activeTab === 'statistics'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <img 
              src="https://unpkg.com/pixelarticons@1.8.0/svg/chart-bar.svg" 
              alt="Statistics" 
              className="w-4 h-4 inline-block mr-2"
              style={{ filter: activeTab === 'statistics' ? 'brightness(0) saturate(100%) invert(1)' : 'brightness(0) saturate(100%) invert(60%)' }}
            />
            <span className="hidden sm:inline">Statistics</span>
            <span className="sm:hidden">Stats</span>
          </button>
          <button
            onClick={() => setActiveTab('diagnostics')}
            className={`px-4 sm:px-6 py-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base ${
              activeTab === 'diagnostics'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <img 
              src="https://unpkg.com/pixelarticons@1.8.0/svg/bug.svg" 
              alt="Diagnostics" 
              className="w-4 h-4 inline-block mr-2"
              style={{ filter: activeTab === 'diagnostics' ? 'brightness(0) saturate(100%) invert(1)' : 'brightness(0) saturate(100%) invert(60%)' }}
            />
            <span className="hidden sm:inline">Diagnostics</span>
            <span className="sm:hidden">Diagnostics</span>
          </button>
          <button
            onClick={() => setActiveTab('storage')}
            className={`px-4 sm:px-6 py-3 font-medium transition-colors whitespace-nowrap text-sm sm:text-base ${
              activeTab === 'storage'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <img 
              src="https://unpkg.com/pixelarticons@1.8.0/svg/cloud.svg" 
              alt="Storage" 
              className="w-4 h-4 inline-block mr-2"
              style={{ filter: activeTab === 'storage' ? 'brightness(0) saturate(100%) invert(1)' : 'brightness(0) saturate(100%) invert(60%)' }}
            />
            <span className="hidden sm:inline">Storage</span>
            <span className="sm:hidden">Storage</span>
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div className="mb-6 bg-red-900/30 border-2 border-dashed border-red-600 rounded-lg p-4 text-red-200">
          {error}
        </div>
      )}
      
      {success && (
        <div className="mb-6 bg-green-900/30 border-2 border-dashed border-green-600 rounded-lg p-4 text-green-200">
          {success}
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'users' && (
        <>
          {/* User Management Content - Keep existing implementation with mobile optimizations */}
          <div className="mb-6 flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex-1 max-w-md w-full">
              <input
                type="text"
                placeholder="Search users (username, email, name, or ID)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-3 bg-gray-800 border-2 border-gray-700 rounded-md text-white focus:border-blue-500 focus:outline-none transition-colors"
              />
            </div>
            <div className="flex flex-wrap gap-4 text-sm w-full lg:w-auto">
              <div className="bg-gray-900 border-2 border-gray-800 rounded-lg px-4 py-2 flex-1 lg:flex-none">
                <span className="text-gray-400">Total Users:</span>
                <span className="text-white font-bold ml-2">{users.length}</span>
              </div>
              <div className="bg-gray-900 border-2 border-gray-800 rounded-lg px-4 py-2 flex-1 lg:flex-none">
                <span className="text-gray-400">Admins:</span>
                <span className="text-red-400 font-bold ml-2">{users.filter(u => u.role === 'admin').length}</span>
              </div>
              <div className="bg-gray-900 border-2 border-gray-800 rounded-lg px-4 py-2 flex-1 lg:flex-none">
                <span className="text-gray-400">Dev Access:</span>
                <span className="text-green-400 font-bold ml-2">{users.filter(u => u.has_dev_access || u.role === 'admin').length}</span>
              </div>
            </div>
          </div>

          {/* User List - Mobile optimized */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              <p className="text-gray-400 mt-2">Loading users...</p>
            </div>
          ) : (
            <div className="bg-gray-900 border-2 border-gray-800 rounded-lg overflow-hidden">
              {/* Mobile-friendly responsive table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-800 border-b border-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Dev Access</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Joined</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {filteredUsers.map((user, index) => (
                      <tr key={user.id} className={`border-b border-gray-800 hover:bg-gray-800/50 transition-colors ${index % 2 === 0 ? 'bg-gray-900/50' : ''}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full overflow-hidden border border-gray-600">
                              <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                                {getUserInitials(user)}
                              </div>
                            </div>
                            <div>
                              <div className="text-white font-medium">{user.full_name || user.username}</div>
                              <div className="text-gray-400 text-sm">@{user.username}</div>
                              <div className="text-gray-500 text-xs font-mono">{user.id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-md text-xs font-medium border ${getRoleColor(user.role)}`}>
                            {user.role?.toUpperCase() || 'USER'}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            {user.role === 'admin' || user.has_dev_access ? (
                              <span className="text-green-400 font-medium">✓ Granted</span>
                            ) : (
                              <span className="text-gray-500">✗ Denied</span>
                            )}
                            {user.role === 'admin' && (
                              <span className="text-xs text-gray-400">(Admin)</span>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="text-gray-400 text-sm">
                            {new Date(user.created_at).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setSelectedUser(user)}
                              className="px-3 py-1 bg-blue-600 text-white hover:bg-blue-700 transition-colors rounded text-sm"
                            >
                              View Details
                            </button>
                            {user.role !== 'admin' && (
                              user.has_dev_access ? (
                                <button
                                  onClick={() => handleRevokeDevAccess(user.id, user.username)}
                                  disabled={actionLoading === user.id}
                                  className="px-3 py-1 bg-red-600 text-white hover:bg-red-700 transition-colors rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {actionLoading === user.id ? 'Revoking...' : 'Revoke Dev'}
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleGrantDevAccess(user.id, user.username)}
                                  disabled={actionLoading === user.id}
                                  className="px-3 py-1 bg-green-600 text-white hover:bg-green-700 transition-colors rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {actionLoading === user.id ? 'Granting...' : 'Grant Dev'}
                                </button>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile card layout */}
              <div className="lg:hidden space-y-4 p-4">
                {filteredUsers.map((user, index) => (
                  <div key={user.id} className={`bg-gray-800 rounded-lg p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 ${index % 2 === 0 ? 'bg-gray-900/50' : ''}`}>
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-12 h-12 rounded-full overflow-hidden border border-gray-600">
                        <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-base">
                          {getUserInitials(user)}
                        </div>
                      </div>
                      <div>
                        <div className="text-white font-medium">{user.full_name || user.username}</div>
                        <div className="text-gray-400 text-xs">@{user.username}</div>
                        <div className="text-gray-500 text-xs font-mono">{user.id}</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 text-sm">
                      <span className={`px-2 py-1 rounded-md text-xs font-medium border ${getRoleColor(user.role)}`}>
                        {user.role?.toUpperCase() || 'USER'}
                      </span>
                      <div className="flex items-center gap-1">
                        {user.role === 'admin' || user.has_dev_access ? (
                          <span className="text-green-400 font-medium text-xs">✓ Granted</span>
                        ) : (
                          <span className="text-gray-500 text-xs">✗ Denied</span>
                        )}
                        {user.role === 'admin' && (
                          <span className="text-xs text-gray-400">(Admin)</span>
                        )}
                      </div>
                      <div className="text-gray-400 text-xs">Joined: {new Date(user.created_at).toLocaleDateString()}</div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSelectedUser(user)}
                          className="px-3 py-1 bg-blue-600 text-white hover:bg-blue-700 transition-colors rounded text-xs"
                        >
                          View
                        </button>
                        {user.role !== 'admin' && (
                          user.has_dev_access ? (
                            <button
                              onClick={() => handleRevokeDevAccess(user.id, user.username)}
                              disabled={actionLoading === user.id}
                              className="px-3 py-1 bg-red-600 text-white hover:bg-red-700 transition-colors rounded text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {actionLoading === user.id ? 'Revoking...' : 'Revoke Dev'}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleGrantDevAccess(user.id, user.username)}
                              disabled={actionLoading === user.id}
                              className="px-3 py-1 bg-green-600 text-white hover:bg-green-700 transition-colors rounded text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {actionLoading === user.id ? 'Granting...' : 'Grant Dev'}
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {filteredUsers.length === 0 && !loading && (
                <div className="text-center py-12 text-gray-400">
                  {searchTerm ? 'No users found matching your search.' : 'No users found.'}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {activeTab === 'plugin-manager' && (
        <PluginVersionManager />
      )}

      {activeTab === 'themes' && (
        <>
          {/* Theme Management Content - Mobile optimized */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
            {/* Current Theme & Selection */}
            <div className="bg-gray-900 border-2 border-gray-800 rounded-lg p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-white mb-4">Active Theme</h2>
              
              {currentTheme && (
                <div className="mb-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
                  <h3 className="text-base sm:text-lg font-medium text-white mb-2">{currentTheme.display_name}</h3>
                  <p className="text-gray-400 text-sm mb-4">{currentTheme.description}</p>
                  
                  {/* Color Preview - Mobile optimized */}
                  <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                    {Object.entries(currentTheme.colors).map(([key, color]) => (
                      <div key={key} className="text-center">
                        <div 
                          className="w-6 h-6 sm:w-8 sm:h-8 rounded border border-gray-600 mx-auto mb-1" 
                          style={{ backgroundColor: color }}
                        ></div>
                        <span className="text-xs text-gray-400 capitalize">{key}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <h3 className="text-base sm:text-lg font-medium text-white mb-4">Available Themes</h3>
              <div className="space-y-3">
                {availableThemes.map((theme) => (
                  <div key={theme.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gray-800 rounded border border-gray-700 gap-3">
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1">
                        <div className="w-4 h-4 rounded border border-gray-600" style={{ backgroundColor: theme.colors.primary }}></div>
                        <div className="w-4 h-4 rounded border border-gray-600" style={{ backgroundColor: theme.colors.secondary }}></div>
                      </div>
                      <div>
                        <div className="text-white font-medium text-sm sm:text-base">{theme.display_name}</div>
                        <div className="text-gray-400 text-xs sm:text-sm">{theme.description}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleSetActiveTheme(theme.name)}
                      disabled={currentTheme?.name === theme.name || actionLoading === theme.name}
                      className="px-3 py-1 bg-blue-600 text-white hover:bg-blue-700 transition-colors rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                    >
                      {actionLoading === theme.name ? 'Setting...' : currentTheme?.name === theme.name ? 'Active' : 'Set Active'}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Create Theme Section - Mobile optimized */}
            <div className="bg-gray-900 border-2 border-gray-800 rounded-lg p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-white mb-4">Create Custom Theme</h2>
              
              <button
                onClick={() => setShowCreateTheme(!showCreateTheme)}
                className="w-full p-3 bg-green-600 text-white hover:bg-green-700 transition-colors rounded mb-4"
              >
                {showCreateTheme ? 'Hide Create Form' : 'Create New Theme'}
              </button>

              {showCreateTheme && (
                <div className="space-y-4">
                  {/* Form fields - mobile optimized */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Theme Name (internal)</label>
                    <input
                      type="text"
                      value={themeForm.name}
                      onChange={(e) => setThemeForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full p-2 sm:p-3 bg-gray-800 border-2 border-gray-700 rounded text-white focus:border-blue-500 focus:outline-none transition-colors"
                      placeholder="my-custom-theme"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Display Name</label>
                    <input
                      type="text"
                      value={themeForm.displayName}
                      onChange={(e) => setThemeForm(prev => ({ ...prev, displayName: e.target.value }))}
                      className="w-full p-2 sm:p-3 bg-gray-800 border-2 border-gray-700 rounded text-white focus:border-blue-500 focus:outline-none transition-colors"
                      placeholder="My Custom Theme"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                    <textarea
                      value={themeForm.description}
                      onChange={(e) => setThemeForm(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full p-2 sm:p-3 bg-gray-800 border-2 border-gray-700 rounded text-white focus:border-blue-500 focus:outline-none transition-colors"
                      rows={2}
                      placeholder="A beautiful custom theme..."
                    />
                  </div>

                  {/* Color Configuration - Mobile optimized */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Colors</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {Object.entries(themeForm.colors).map(([key, value]) => (
                        <div key={key}>
                          <label className="block text-xs text-gray-400 mb-1 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={value}
                              onChange={(e) => setThemeForm(prev => ({
                                ...prev,
                                colors: { ...prev.colors, [key]: e.target.value }
                              }))}
                              className="w-10 h-8 sm:w-12 sm:h-10 border-2 border-gray-700 rounded"
                            />
                            <input
                              type="text"
                              value={value}
                              onChange={(e) => setThemeForm(prev => ({
                                ...prev,
                                colors: { ...prev.colors, [key]: e.target.value }
                              }))}
                              className="flex-1 p-2 bg-gray-800 border-2 border-gray-700 rounded text-white text-sm focus:border-blue-500 focus:outline-none transition-colors"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={handleCreateTheme}
                    disabled={actionLoading === 'create-theme'}
                    className="w-full p-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading === 'create-theme' ? 'Creating Theme...' : 'Create Theme'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {activeTab === 'statistics' && (
        <AdminStatistics />
      )}

      {activeTab === 'plugin-banners' && (
        <PluginBannerManager />
      )}

      {activeTab === 'diagnostics' && (
        <PluginDiagnostic />
      )}

      {activeTab === 'storage' && (
        <div className="space-y-6">
          <StorageTest />
          <StorageBucketFix />
        </div>
      )}

      {/* User Details Modal - Keep existing implementation */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border-2 border-gray-700 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Keep existing modal content */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">User Details</h2>
              <button
                onClick={() => setSelectedUser(null)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <img 
                  src="https://unpkg.com/pixelarticons@1.8.0/svg/close.svg" 
                  alt="Close" 
                  className="w-6 h-6"
                  style={{ filter: `brightness(0) saturate(100%) invert(60%)` }}
                />
              </button>
            </div>

            <div className="space-y-6">
              {/* Profile Section */}
              <div className="flex items-center gap-4 p-4 bg-gray-800 rounded-lg">
                <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-gray-600">
                  <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
                    {getUserInitials(selectedUser)}
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{selectedUser.full_name || selectedUser.username}</h3>
                  <p className="text-gray-400">@{selectedUser.username}</p>
                  <p className="text-gray-500 text-sm">{selectedUser.email}</p>
                </div>
              </div>

              {/* User Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">User ID</label>
                    <div className="p-2 bg-gray-800 rounded text-gray-300 font-mono text-sm">{selectedUser.id}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Role</label>
                    <span className={`inline-block px-2 py-1 rounded-md text-xs font-medium border ${getRoleColor(selectedUser.role)}`}>
                      {selectedUser.role?.toUpperCase() || 'USER'}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Dev Access</label>
                    <span className={`text-sm font-medium ${selectedUser.role === 'admin' || selectedUser.has_dev_access ? 'text-green-400' : 'text-red-400'}`}>
                      {selectedUser.role === 'admin' || selectedUser.has_dev_access ? '✓ Granted' : '✗ Denied'}
                    </span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Joined</label>
                    <div className="p-2 bg-gray-800 rounded text-gray-300 text-sm">
                      {new Date(selectedUser.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Last Updated</label>
                    <div className="p-2 bg-gray-800 rounded text-gray-300 text-sm">
                      {new Date(selectedUser.updated_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}; 